import React from 'react';
import { i18n } from 'i18n';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSceneLegend } from '../SceneLegend/SceneLegendProvider';
import type { Model } from '../../hooks/useModelState';
import {
  STOPE_LEGEND_PROPERTY_OPTIONS,
  buildDefaultStopeLegendConfig,
  buildStopeLegendSource,
  createEmptyStopeLegendBin,
  formatStopeLegendEditorValue,
  getStopeLegendItems,
  getStopeLegendPropertyOption,
  getStopeLegendTitle,
  loadSavedStopeLegends,
  parseStopeLegendEditorValue,
  saveSavedStopeLegends
} from './stopeLegend';
import type { StopeLegendBin, StopeLegendBinMode, StopeLegendConfig } from './stopeLegend';

interface StopeLegendBuilderDialogProps {
  open: boolean;
  model: Model;
  onClose: () => void;
}

const DEFAULT_BIN_COUNT = 7;

const cloneConfig = (config: StopeLegendConfig): StopeLegendConfig => JSON.parse(JSON.stringify(config));

const StopeLegendBuilderDialog = ({
  open,
  model,
  onClose,
}: StopeLegendBuilderDialogProps) => {
  const projectId = model.project?.entityId ?? '';
  const { setLegendData, setShowLegend } = useSceneLegend();
  const [savedLegends, setSavedLegends] = React.useState(loadSavedStopeLegends(projectId));
  const [draft, setDraft] = React.useState<StopeLegendConfig | null>(null);
  const [binCount, setBinCount] = React.useState(DEFAULT_BIN_COUNT);

  const stopeSources = React.useMemo(() => {
    const overviewByStopeId = new Map(
      (model.mineOverview ?? []).map((summary) => [summary.stopeIdentifier.stopeId, summary]),
    );

    return Object.values(model.stopes ?? {})
      .flat()
      .filter((stope) => !!stope?.data)
      .map((stope) => buildStopeLegendSource(stope.data, overviewByStopeId.get(stope.data.stopeId)));
  }, [model.stopes, model.mineOverview]);

  const createDefaultDraft = React.useCallback((
    propertyId = STOPE_LEGEND_PROPERTY_OPTIONS[0].id,
    mode?: StopeLegendBinMode,
    existingConfig?: StopeLegendConfig | null,
  ) => buildDefaultStopeLegendConfig(
    stopeSources,
    propertyId,
    mode ?? getStopeLegendPropertyOption(propertyId).defaultBinMode,
    binCount,
    existingConfig,
  ), [binCount, stopeSources]);

  React.useEffect(() => {
    if (!open) return;

    const saved = loadSavedStopeLegends(projectId);
    const active = saved.legends.find((legend) => legend.id === saved.activeLegendId);
    const defaultProperty = STOPE_LEGEND_PROPERTY_OPTIONS[0];
    setSavedLegends(saved);
    setBinCount(DEFAULT_BIN_COUNT);
    setDraft(active ? cloneConfig(active) : buildDefaultStopeLegendConfig(
      stopeSources,
      defaultProperty.id,
      defaultProperty.defaultBinMode,
      DEFAULT_BIN_COUNT,
    ));
  }, [open, projectId, stopeSources]);

  const property = draft ? getStopeLegendPropertyOption(draft.propertyId) : STOPE_LEGEND_PROPERTY_OPTIONS[0];
  const selectedSavedLegendId = draft && savedLegends.legends.some((legend) => legend.id === draft.id) ? draft.id : '';

  const applyLegend = React.useCallback((legend: StopeLegendConfig | null) => {
    model.setStopeLegendConfig(legend);

    if (legend) {
      if (model.showStopeShapes) {
        setLegendData({
          title: getStopeLegendTitle(legend),
          items: getStopeLegendItems(legend),
        });
        setShowLegend(true);
      }
      return;
    }

    setLegendData(null);
    setShowLegend(false);
  }, [model, setLegendData, setShowLegend]);

  const persist = React.useCallback((legend: StopeLegendConfig) => {
    if (!projectId) return;

    const nextSaved = {
      activeLegendId: legend.id,
      legends: [
        ...savedLegends.legends.filter((item) => item.id !== legend.id),
        legend,
      ],
    };

    saveSavedStopeLegends(projectId, nextSaved);
    setSavedLegends(nextSaved);
    applyLegend(legend);
  }, [applyLegend, projectId, savedLegends.legends]);

  const handleSavedLegendChange = (event: SelectChangeEvent<string>) => {
    const legend = savedLegends.legends.find((item) => item.id === event.target.value);
    if (legend) setDraft(cloneConfig(legend));
  };

  const handleNewLegend = () => {
    setDraft(createDefaultDraft());
  };

  const handleDeleteLegend = () => {
    if (!projectId || !draft) return;

    const nextSaved = {
      activeLegendId: savedLegends.activeLegendId === draft.id ? null : savedLegends.activeLegendId,
      legends: savedLegends.legends.filter((legend) => legend.id !== draft.id),
    };
    saveSavedStopeLegends(projectId, nextSaved);
    setSavedLegends(nextSaved);

    if (savedLegends.activeLegendId === draft.id) {
      applyLegend(null);
    }

    setDraft(createDefaultDraft());
  };

  const handlePropertyChange = (event: SelectChangeEvent<string>) => {
    if (!draft) return;
    const nextProperty = getStopeLegendPropertyOption(event.target.value);
    setDraft(createDefaultDraft(nextProperty.id, nextProperty.defaultBinMode, {
      ...draft,
      propertyId: nextProperty.id,
      binMode: nextProperty.defaultBinMode,
    }));
  };

  const handleModeChange = (_event: React.MouseEvent<HTMLElement>, value: StopeLegendBinMode | null) => {
    if (!draft || !value) return;
    setDraft(createDefaultDraft(draft.propertyId, value, { ...draft, binMode: value }));
  };

  const handleGenerateBins = () => {
    if (!draft) return;
    setDraft(createDefaultDraft(draft.propertyId, draft.binMode, draft));
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDraft((current) => (current ? { ...current, name: event.target.value } : current));
  };

  const updateBin = (binId: string, updates: Partial<StopeLegendBin>) => {
    setDraft((current) => (current ? {
      ...current,
      bins: current.bins.map((bin) => (bin.id === binId ? { ...bin, ...updates } : bin)),
    } : current));
  };

  const deleteBin = (binId: string) => {
    setDraft((current) => (current ? {
      ...current,
      bins: current.bins.filter((bin) => bin.id !== binId),
    } : current));
  };

  const handleAddBin = () => {
    setDraft((current) => (current ? {
      ...current,
      bins: [
        ...current.bins,
        createEmptyStopeLegendBin(current),
      ],
    } : current));
  };

  const handleSave = () => {
    if (!draft) return;
    persist(draft);
    onClose();
  };

  const handleApply = () => {
    if (!draft) return;
    applyLegend(draft);
  };

  const handleClear = () => {
    if (projectId) {
      const nextSaved = {
        activeLegendId: null,
        legends: savedLegends.legends,
      };
      saveSavedStopeLegends(projectId, nextSaved);
      setSavedLegends(nextSaved);
    }
    applyLegend(null);
  };

  const canDelete = !!draft && savedLegends.legends.some((legend) => legend.id === draft.id);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{i18n._('Stope Legend Builder')}</DialogTitle>
      <DialogContent dividers>
        {draft && (
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="saved-stope-legend-label">{i18n._('Saved legend')}</InputLabel>
                <Select
                  labelId="saved-stope-legend-label"
                  value={selectedSavedLegendId}
                  label={i18n._('Saved legend')}
                  onChange={handleSavedLegendChange}
                >
                  <MenuItem value="">{i18n._('Unsaved legend')}</MenuItem>
                  {savedLegends.legends.map((legend) => (
                    <MenuItem key={legend.id} value={legend.id}>{legend.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title={i18n._('New legend')}>
                <IconButton size="small" onClick={handleNewLegend}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={i18n._('Delete legend')}>
                <span>
                  <IconButton size="small" onClick={handleDeleteLegend} disabled={!canDelete}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <TextField
                label={i18n._('Name')}
                size="small"
                value={draft.name}
                onChange={handleNameChange}
                sx={{ flex: 1, minWidth: 220 }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel id="stope-legend-property-label">{i18n._('Property')}</InputLabel>
                <Select
                  labelId="stope-legend-property-label"
                  value={draft.propertyId}
                  label={i18n._('Property')}
                  onChange={handlePropertyChange}
                >
                  {STOPE_LEGEND_PROPERTY_OPTIONS.map((option) => (
                    <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <ToggleButtonGroup
                size="small"
                exclusive
                value={draft.binMode}
                onChange={handleModeChange}
              >
                <ToggleButton value="range" disabled={property.kind === 'category'}>{i18n._('Range bins')}</ToggleButton>
                <ToggleButton value="value">{i18n._('Value bins')}</ToggleButton>
              </ToggleButtonGroup>

              {draft.binMode === 'range' && (
                <TextField
                  label={i18n._('Bins')}
                  size="small"
                  type="number"
                  value={binCount}
                  onChange={(event) => setBinCount(Number(event.target.value))}
                  inputProps={{ min: 2, max: 20 }}
                  sx={{ width: 96 }}
                />
              )}

              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleGenerateBins}
              >
                Generate
              </Button>
            </Stack>

            <Divider />

            <TableContainer sx={{ maxHeight: 430 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 130 }}>{i18n._('Color')}</TableCell>
                    {draft.binMode === 'range' ? (
                      <>
                        <TableCell sx={{ width: 180 }}>From</TableCell>
                        <TableCell sx={{ width: 180 }}>To</TableCell>
                      </>
                    ) : (
                      <TableCell sx={{ width: 220 }}>{i18n._('Value')}</TableCell>
                    )}
                    <TableCell>{i18n._('Label')}</TableCell>
                    <TableCell align="right" sx={{ width: 56 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {draft.bins.map((bin) => (
                    <TableRow key={bin.id}>
                      <TableCell>
                        <TextField
                          type="color"
                          size="small"
                          value={bin.color}
                          onChange={(event) => updateBin(bin.id, { color: event.target.value })}
                          inputProps={{ 'aria-label': 'Bin color' }}
                          sx={{ width: 96 }}
                        />
                      </TableCell>
                      {draft.binMode === 'range' ? (
                        <>
                          <TableCell>
                            <TextField
                              size="small"
                              type={property.kind === 'date' ? 'date' : 'number'}
                              value={formatStopeLegendEditorValue(draft.propertyId, bin.min)}
                              onChange={(event) => updateBin(bin.id, {
                                min: parseStopeLegendEditorValue(draft.propertyId, event.target.value) as number | undefined,
                              })}
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type={property.kind === 'date' ? 'date' : 'number'}
                              value={formatStopeLegendEditorValue(draft.propertyId, bin.max)}
                              onChange={(event) => updateBin(bin.id, {
                                max: parseStopeLegendEditorValue(draft.propertyId, event.target.value) as number | undefined,
                              })}
                              fullWidth
                            />
                          </TableCell>
                        </>
                      ) : (
                        <TableCell>
                          <TextField
                            size="small"
                            value={formatStopeLegendEditorValue(draft.propertyId, bin.value)}
                            onChange={(event) => updateBin(bin.id, {
                              value: parseStopeLegendEditorValue(draft.propertyId, event.target.value),
                            })}
                            fullWidth
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <TextField
                          size="small"
                          value={bin.label}
                          onChange={(event) => updateBin(bin.id, { label: event.target.value })}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => deleteBin(bin.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box>
              <Button variant="text" size="small" startIcon={<AddIcon />} onClick={handleAddBin}>
                Add bin
              </Button>
              {draft.bins.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1, display: 'inline' }}>
                  No matching values.
                </Typography>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClear}>{i18n._('Clear')}</Button>
        <Button onClick={handleApply} disabled={!draft}>{i18n._('Apply')}</Button>
        <Button onClick={handleSave} disabled={!draft} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StopeLegendBuilderDialog;
